import { useState, useEffect, useRef } from 'react';
import { eventBus } from '@/map/core/events';
import { pauseClock } from '@/map/core/pause';
import { motion, AnimatePresence } from 'framer-motion';
import {
  CloudRain, Radio, Truck, Users, AlertTriangle,
  CheckCircle2, Activity, MapPin, Zap, Clock, Gauge
} from 'lucide-react';
import { useAppStore } from '@/stores/appStore';
import { getScenarioById } from '@/map/scenario/definitions/index';

interface Metric {
  id: string;
  kind: 'countdown' | 'counter' | 'burndown' | 'rag-legend';
  label: string;
  value: number;
  display: number; // eased value actually shown
  target?: number;
  unit?: string;
  severity?: string;
}

interface HUDEvent {
  id: string;
  time: string;
  icon: string;
  title: string;
  detail: string;
  severity: string;
}

interface AffectedRegion {
  name: string;
  status: string;
  properties: number;
}

interface TeamMovement {
  name: string;
  status: string;
  eta?: string;
  destination: string;
}

interface WeatherData {
  windSpeed: number;
  rainfall: number;
  pressure: number;
}

export function ScenarioHUD() {
  const activeScenario = useAppStore((s) => s.scenarioState.activeScenario);
  const [visible, setVisible] = useState(false);
  const [events, setEvents] = useState<HUDEvent[]>([]);
  const [regions, setRegions] = useState<AffectedRegion[]>([]);
  const [teams, setTeams] = useState<TeamMovement[]>([]);
  const [weather, setWeather] = useState<WeatherData | null>(null);
  const [elapsed, setElapsed] = useState(0);
  const [metrics, setMetrics] = useState<Metric[]>([]);
  const [scenarioName, setScenarioName] = useState('Live Incident');
  const [hudLabel, setHudLabel] = useState('LIVE INCIDENT');
  const metricsRef = useRef<Metric[]>([]);
  metricsRef.current = metrics;

  useEffect(() => {
    if (!activeScenario) {
      // Small delay before hiding to allow fade out
      const t = setTimeout(() => {
        setVisible(false);
        setEvents([]);
        setRegions([]);
        setTeams([]);
        setWeather(null);
        setElapsed(0);
        setMetrics([]);
        setHudLabel('LIVE INCIDENT');
        setScenarioName('Live Incident');
      }, 500);
      return () => clearTimeout(t);
    }

    setVisible(true);
    // Resolve hudLabel from the scenario definition directly (avoids race).
    const def = getScenarioById(activeScenario);
    if (def?.hudLabel) setHudLabel(def.hudLabel);
    else setHudLabel('LIVE INCIDENT');
    if (def?.title) setScenarioName(def.title);

    // Elapsed advances only while the scenario is running (pause clock).
    // Same tick drives countdown metrics so they freeze on pause too.
    const timer = setInterval(() => {
      if (pauseClock.paused) return;
      setElapsed((e) => e + 1);
      setMetrics((prev) =>
        prev.map((m) =>
          m.kind === 'countdown' && m.value > 0
            ? { ...m, value: m.value - 1 }
            : m,
        ),
      );
    }, 1000);

    // Smoothly ease counter / burndown displays toward their target value.
    let raf = 0;
    const ease = () => {
      if (!pauseClock.paused) {
        setMetrics((prev) => {
          let changed = false;
          const next = prev.map((m) => {
            if (m.kind === 'countdown' || m.kind === 'rag-legend') return m;
            const d = m.value - m.display;
            if (Math.abs(d) < 0.5) return m;
            changed = true;
            return { ...m, display: m.display + d * 0.08 };
          });
          return changed ? next : prev;
        });
      }
      raf = requestAnimationFrame(ease);
    };
    raf = requestAnimationFrame(ease);

    // Subscribe to EventBus
    const unsub1 = eventBus.on('scenario:start', (d: any) => {
      setVisible(true);
      if (d?.title) setScenarioName(d.title);
      if (d?.hudLabel) setHudLabel(d.hudLabel);
      else setHudLabel('LIVE INCIDENT');
    });
    const unsub2 = eventBus.on('scenario:end', () => {
      setTimeout(() => {
        setVisible(false);
        setEvents([]);
        setRegions([]);
        setTeams([]);
        setWeather(null);
        setMetrics([]);
      }, 2000);
    });
    const unsubM = eventBus.on('hud:metric', (d: any) => {
      setMetrics((prev) => {
        const existing = prev.find((m) => m.id === d.id);
        const m: Metric = {
          id: d.id,
          kind: d.kind,
          label: d.label,
          value: d.value,
          display: existing ? existing.display : d.value,
          target: d.target,
          unit: d.unit,
          severity: d.severity,
        };
        return existing ? prev.map((x) => (x.id === d.id ? m : x)) : [...prev, m];
      });
    });
    const unsubMC = eventBus.on('hud:metric-clear', (d: any) => {
      setMetrics((prev) => prev.filter((m) => m.id !== d.id));
    });
    const unsub3 = eventBus.on('hud:event', (detail: any) => {
      const now = new Date();
      const time = `${now.getHours().toString().padStart(2, '0')}:${now.getMinutes().toString().padStart(2, '0')}:${now.getSeconds().toString().padStart(2, '0')}`;
      setEvents((prev) => [{ ...detail, id: `${Date.now()}-${Math.random()}`, time }, ...prev]);
    });
    const unsub4 = eventBus.on('hud:region', (detail: any) => setRegions(detail.regions));
    const unsub5 = eventBus.on('hud:team', (detail: any) => setTeams(detail.teams));
    const unsub6 = eventBus.on('hud:weather', (detail: any) => setWeather(detail));

    return () => {
      clearInterval(timer);
      cancelAnimationFrame(raf);
      unsub1(); unsub2(); unsub3(); unsub4(); unsub5(); unsub6();
      unsubM(); unsubMC();
    };
  }, [activeScenario]);

  if (!visible) return null;

  const severityColor = (s: string) => {
    switch (s) {
      case 'critical': return 'text-red-400';
      case 'warning': return 'text-amber-400';
      case 'success': return 'text-emerald-400';
      default: return 'text-sky-400';
    }
  };

  const severityBorder = (s: string) => {
    switch (s) {
      case 'critical': return 'border-l-red-500/50';
      case 'warning': return 'border-l-amber-500/50';
      case 'success': return 'border-l-emerald-500/50';
      default: return 'border-l-sky-500/50';
    }
  };

  const statusDot = (s: string) => {
    switch (s) {
      case 'critical': return 'bg-red-500';
      case 'at-risk': return 'bg-amber-500';
      default: return 'bg-sky-500';
    }
  };

  const teamColor = (s: string) => {
    switch (s) {
      case 'on-site': return 'text-emerald-400';
      case 'en-route': return 'text-amber-400';
      case 'dispatched': return 'text-sky-400';
      default: return 'text-slate-500';
    }
  };

  const iconMap: Record<string, typeof Zap> = {
    storm: CloudRain, sensor: Radio, dispatch: Truck,
    crew: Users, alert: AlertTriangle, resolve: CheckCircle2,
  };

  const formatElapsed = (s: number) => {
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return `${m}:${sec.toString().padStart(2, '0')}`;
  };

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          className="absolute top-0 left-0 h-full w-[300px] pointer-events-auto z-20"
          initial={{ x: -300, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          exit={{ x: -300, opacity: 0 }}
          transition={{ duration: 0.6, ease: 'easeOut' }}
        >
          <div className="h-full bg-[var(--panel-bg)] backdrop-blur-2xl border-r border-[var(--border)] flex flex-col">

            {/* Header */}
            <div className="p-4 border-b border-[var(--border)] shrink-0">
              <div className="flex items-center gap-2">
                <div className={`w-2 h-2 rounded-full animate-pulse ${hudLabel === 'LIVE INCIDENT' ? 'bg-red-500' : 'bg-amber-500'}`} />
                <span className={`text-[10px] font-bold tracking-[0.2em] ${hudLabel === 'LIVE INCIDENT' ? 'text-red-400' : 'text-amber-400'}`}>{hudLabel}</span>
                <span className="ml-auto text-[10px] text-[var(--text-muted)] font-mono flex items-center gap-1">
                  <Clock size={10} />
                  {formatElapsed(elapsed)}
                </span>
              </div>
              <h2 className="text-[var(--text-primary)] font-semibold text-sm mt-1">{scenarioName}</h2>
              <p className="text-[var(--text-secondary)] text-[10px] mt-0.5">North Vale • Active</p>
            </div>

            {/* Weather */}
            <AnimatePresence>
              {weather && (
                <motion.div
                  className="p-3 border-b border-[var(--border)] shrink-0"
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                >
                  <div className="flex items-center gap-1.5 mb-2">
                    <CloudRain size={11} className="text-sky-400" />
                    <span className="text-[10px] font-semibold text-sky-400 tracking-widest">WEATHER</span>
                  </div>
                  <div className="grid grid-cols-3 gap-1.5">
                    {[
                      { label: 'Wind', value: weather.windSpeed, unit: 'mph' },
                      { label: 'Rain', value: weather.rainfall, unit: 'mm/h' },
                      { label: 'Pressure', value: weather.pressure, unit: 'mb' },
                    ].map((d) => (
                      <div key={d.label} className="bg-[var(--badge-bg)] rounded-lg p-1.5 text-center">
                        <p className="text-[10px] text-[var(--text-secondary)]">{d.label}</p>
                        <p className="text-xs font-bold text-[var(--text-primary)]">{d.value}<span className="text-[10px] text-[var(--text-secondary)] ml-0.5">{d.unit}</span></p>
                      </div>
                    ))}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Scenario metrics (countdown / counter / burndown / RAG) */}
            <AnimatePresence>
              {metrics.length > 0 && (
                <motion.div
                  className="p-3 border-b border-[var(--border)] shrink-0 space-y-2"
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                >
                  <div className="flex items-center gap-1.5 mb-1">
                    <Gauge size={11} className="text-teal-400" />
                    <span className="text-[10px] font-semibold text-teal-400 tracking-widest">METRICS</span>
                  </div>
                  {metrics.map((m) => (
                    <MetricWidget key={m.id} m={m} />
                  ))}
                </motion.div>
              )}
            </AnimatePresence>

            {/* Affected Regions */}
            <AnimatePresence>
              {regions.length > 0 && (
                <motion.div
                  className="p-3 border-b border-[var(--border)] shrink-0"
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                >
                  <div className="flex items-center gap-1.5 mb-2">
                    <MapPin size={11} className="text-amber-400" />
                    <span className="text-[10px] font-semibold text-amber-400 tracking-widest">AFFECTED REGIONS</span>
                  </div>
                  <div className="space-y-1">
                    {regions.map((r) => (
                      <div key={r.name} className="flex items-center justify-between bg-[var(--badge-bg)] rounded px-2 py-1">
                        <div className="flex items-center gap-1.5">
                          <div className={`w-1.5 h-1.5 rounded-full ${statusDot(r.status)}`} />
                          <span className="text-xs text-[var(--text-primary)]">{r.name}</span>
                        </div>
                        <span className="text-[11px] text-[var(--text-secondary)]">{r.properties} props</span>
                      </div>
                    ))}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Teams */}
            <AnimatePresence>
              {teams.length > 0 && (
                <motion.div
                  className="p-3 border-b border-[var(--border)] shrink-0"
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                >
                  <div className="flex items-center gap-1.5 mb-2">
                    <Truck size={11} className="text-teal-400" />
                    <span className="text-[10px] font-semibold text-teal-400 tracking-widest">TEAMS</span>
                  </div>
                  <div className="space-y-1">
                    {teams.map((t) => (
                      <div key={t.name} className="flex items-center justify-between bg-[var(--badge-bg)] rounded px-2 py-1">
                        <div>
                          <p className="text-xs text-[var(--text-primary)]">{t.name}</p>
                          <p className="text-[11px] text-[var(--text-secondary)]">&rarr; {t.destination}</p>
                        </div>
                        <div className="text-right">
                          <p className={`text-xs font-medium ${teamColor(t.status)}`}>{t.status.toUpperCase()}</p>
                          {t.eta && <p className="text-[11px] text-[var(--text-secondary)]">{t.eta}</p>}
                        </div>
                      </div>
                    ))}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Event Feed */}
            <div className="flex-1 overflow-hidden flex flex-col min-h-0">
              <div className="p-3 pb-1 shrink-0">
                <div className="flex items-center gap-1.5">
                  <Activity size={11} className="text-[var(--text-secondary)]" />
                  <span className="text-[10px] font-semibold text-[var(--text-secondary)] tracking-widest">EVENT FEED</span>
                  <span className="ml-auto text-[11px] text-[var(--text-muted)]">{events.length} events</span>
                </div>
              </div>
              <div className="flex-1 overflow-y-auto px-3 pb-3 space-y-1.5">
                <AnimatePresence>
                  {events.map((event) => {
                    const Icon = iconMap[event.icon] || Zap;
                    return (
                      <motion.div
                        key={event.id}
                        className={`p-2 rounded-lg bg-[var(--badge-bg)] border-l-2 ${severityBorder(event.severity)}`}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.3 }}
                      >
                        <div className="flex items-start gap-1.5">
                          <Icon size={11} className={`${severityColor(event.severity)} mt-0.5 shrink-0`} />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between">
                              <p className={`text-xs font-semibold ${severityColor(event.severity)}`}>{event.title}</p>
                              <span className="text-[11px] text-[var(--text-muted)] font-mono">{event.time}</span>
                            </div>
                            <p className="text-[11px] text-[var(--text-secondary)] mt-0.5 leading-relaxed">{event.detail}</p>
                          </div>
                        </div>
                      </motion.div>
                    );
                  })}
                </AnimatePresence>
              </div>
            </div>

            {/* Footer */}
            <div className="p-3 border-t border-[var(--border)] shrink-0">
              <div className="grid grid-cols-3 gap-1.5 text-center">
                <div>
                  <p className="text-[10px] text-[var(--text-secondary)]">Elapsed</p>
                  <p className="text-sm font-bold text-[var(--text-primary)] font-mono">{formatElapsed(elapsed)}</p>
                </div>
                <div>
                  <p className="text-[10px] text-[var(--text-secondary)]">Affected</p>
                  <p className="text-sm font-bold text-amber-400">{regions.reduce((a, r) => a + r.properties, 0)}</p>
                </div>
                <div>
                  <p className="text-[10px] text-[var(--text-secondary)]">Status</p>
                  <p className="text-sm font-bold text-red-400">ACTIVE</p>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

// ── Reusable scenario metric widget ────────────────────────────────────────
// Driven by `hud-metric` steps. One component, four presentations:
//   countdown  — a ticking SLA / permit timer (mm:ss), colours by urgency
//   counter    — an animated tally (customers notified, carbon saved)
//   burndown   — value-against-target progress bar (budget / forecast)
//   rag-legend — Red/Amber/Green portfolio legend (value packed as RRGGAA…)
function MetricWidget({ m }: { m: Metric }) {
  const sevText =
    m.severity === 'critical' ? 'text-red-400'
    : m.severity === 'warning' ? 'text-amber-400'
    : m.severity === 'success' ? 'text-emerald-400'
    : 'text-sky-400';

  if (m.kind === 'countdown') {
    const v = Math.max(0, Math.round(m.value));
    const mm = Math.floor(v / 60);
    const ss = (v % 60).toString().padStart(2, '0');
    const urgent = v <= 60;
    return (
      <div className="flex items-center justify-between bg-[var(--badge-bg)] rounded-lg px-2.5 py-2">
        <span className="text-[11px] text-[var(--text-secondary)]">{m.label}</span>
        <span className={`text-sm font-bold font-mono ${urgent ? 'text-red-400 animate-pulse' : sevText}`}>
          {mm}:{ss}
        </span>
      </div>
    );
  }

  if (m.kind === 'burndown') {
    const target = m.target ?? Math.max(1, m.value);
    const pct = Math.max(0, Math.min(100, (m.display / target) * 100));
    const bar =
      pct > 85 ? 'bg-red-500' : pct > 60 ? 'bg-amber-500' : 'bg-emerald-500';
    return (
      <div className="bg-[var(--badge-bg)] rounded-lg px-2.5 py-2">
        <div className="flex items-center justify-between mb-1">
          <span className="text-[11px] text-[var(--text-secondary)]">{m.label}</span>
          <span className="text-[11px] font-mono text-[var(--text-primary)]">
            {Math.round(m.display).toLocaleString()}{m.unit ? ` ${m.unit}` : ''} / {target.toLocaleString()}
          </span>
        </div>
        <div className="h-1.5 rounded-full bg-[var(--card-bg)] overflow-hidden">
          <div className={`h-full rounded-full ${bar} transition-all duration-300`} style={{ width: `${pct}%` }} />
        </div>
      </div>
    );
  }

  if (m.kind === 'rag-legend') {
    return (
      <div className="flex items-center justify-between bg-[var(--badge-bg)] rounded-lg px-2.5 py-2">
        <span className="text-[11px] text-[var(--text-secondary)]">{m.label}</span>
        <div className="flex items-center gap-2.5">
          {[
            { c: 'bg-red-500', n: Math.floor(m.value / 10000) },
            { c: 'bg-amber-500', n: Math.floor((m.value % 10000) / 100) },
            { c: 'bg-emerald-500', n: m.value % 100 },
          ].map((g, i) => (
            <span key={i} className="flex items-center gap-1">
              <span className={`w-1.5 h-1.5 rounded-full ${g.c}`} />
              <span className="text-[11px] font-mono text-[var(--text-primary)]">{g.n}</span>
            </span>
          ))}
        </div>
      </div>
    );
  }

  // counter
  return (
    <div className="flex items-center justify-between bg-[var(--badge-bg)] rounded-lg px-2.5 py-2">
      <span className="text-[11px] text-[var(--text-secondary)]">{m.label}</span>
      <span className={`text-sm font-bold font-mono ${sevText}`}>
        {Math.round(m.display).toLocaleString()}
        {m.unit ? <span className="text-[10px] text-[var(--text-secondary)] ml-1">{m.unit}</span> : null}
      </span>
    </div>
  );
}
